/**
 * Schedule Shared Utilities (Phase 5 Vague A)
 *
 * ESM replacement for the 338-LOC inline ``{% include %}`` that used to
 * live in ``templates/office/schedule/_shared_utils.html`` (loaded inside
 * the bottom ``<script>`` of ``schedule_hub.html``).
 *
 * Bootstraps the cross-view ``window.ScheduleHub`` namespace consumed by
 * every schedule surface (Calendar / Team / Quick Create) and by a few
 * outside-of-schedule modules that need to refresh the active view after
 * a successful AJAX submit:
 *
 *   - frontend/src/utils/forms/drawer-action-form.js → calls
 *     ``window.ScheduleHub.refresh.team()`` after visit-action drawers
 *     succeed (no-op when not on the team view).
 *   - frontend/src/schedule-calendar.js → reads
 *     ``window.ScheduleHub.quickCreate.show`` and
 *     ``window.ScheduleHub.config.maxVisibleEvents``, and registers
 *     ``window.ScheduleHub.refresh.calendar``.
 *   - templates/office/visits/partials/_detail_drawer.html → calls
 *     ``window.ScheduleHub.refresh.team()`` from the inline assignment
 *     submit handler.
 *
 * The namespace is therefore part of the public cross-bundle API and
 * must keep the same shape:
 *
 *   window.ScheduleHub = {
 *       config:    { maxVisibleEvents: number },
 *       utils:     { getCsrfToken, escapeHtml, escapeJsAttr, skipNextWsRefresh },
 *       overflow:  { render(container, events, options) },
 *       dayDetail: { show(opts), hide() },
 *       refresh:   { calendar?, team? },   // populated by view modules
 *       quickCreate: { ... },              // populated by Vague C
 *   }
 *
 * Bootstrap behaviour
 * -------------------
 * Three side effects are NOT idempotent in the legacy template:
 *
 *   1. The day-detail drawer click + Escape document-level listeners
 *      (``ScheduleHub.dayDetail`` IIFE in the legacy file).
 *   2. The schedule WebSocket subscription. Since the realtime
 *      generalisation (``utils/realtime.js``), the WS connection itself
 *      is opened once per session by ``bootstrapRealtime()`` in app.js;
 *      this module only attaches a ``ws:schedule_changed`` listener and
 *      registers the ``skipNextWsRefresh`` hook used by Team drag/drop.
 *   3. The ``window.OVERDUE_THRESHOLD_MINUTES`` / ``window.CURRENT_LANGUAGE``
 *      reads — those come from the head block in schedule_hub.html and
 *      are independent of this module.
 *
 * The legacy template re-runs the IIFEs on every full page render,
 * which actually opens a fresh WS connection AND attaches a fresh pair
 * of document listeners. Boosted HTMX navigation on schedule pages is
 * disabled (see ``htmx:beforeSwap`` in app.js), so the leak only
 * happens once per user-visible reload — unnoticed in practice.
 *
 * This module guards against re-init via module-scoped flags, so even
 * if ``initScheduleShared`` is called multiple times (e.g. via an
 * ``htmx:afterSwap`` re-init pass) the listeners are only attached
 * once per browser session.
 */

let _dayDetailListenersBootstrapped = false;
let _wsListenerBound = false;

function ensureNamespace() {
  window.ScheduleHub = window.ScheduleHub || {};
  window.ScheduleHub.utils = window.ScheduleHub.utils || {};
  window.ScheduleHub.quickCreate = window.ScheduleHub.quickCreate || {};
  window.ScheduleHub.refresh = window.ScheduleHub.refresh || {};
  window.ScheduleHub.overflow = window.ScheduleHub.overflow || {};
  window.ScheduleHub.dayDetail = window.ScheduleHub.dayDetail || {};
  window.ScheduleHub.config = window.ScheduleHub.config || {
    // Shared "+N more" threshold — Calendar uses FullCalendar's native
    // dayMaxEvents/popover, Team uses ScheduleHub.overflow.render.
    maxVisibleEvents: 4,
  };
}

/**
 * Read a JSON payload rendered by Django's ``{{ x|json_script:"id" }}``
 * filter (``<script type="application/json" id="...">``). Returns an
 * empty object (not null) on miss / parse error so callers can use
 * ``Object.assign`` / spread without extra guards.
 */
function readJsonScript(id) {
  const el = document.getElementById(id);
  if (!el) return {};
  try {
    return JSON.parse(el.textContent) || {};
  } catch (e) {
    console.error(`schedule-shared: failed to parse #${id}`, e);
    return {};
  }
}

/**
 * Hydrate the cross-bundle ``window.*`` globals consumed by
 * schedule-calendar.js (read at module load, BEFORE any auto-init), the
 * team grid module, the quick-create drawer, and schedule-rendering.
 *
 * - ``window.SCHEDULE_TRANSLATIONS`` and ``window.TRANSLATIONS`` come
 *   from JSON blocks rendered by Django (``schedule-translations-map``
 *   / ``schedule-shared-translations``).
 * - ``window.OVERDUE_THRESHOLD_MINUTES`` and ``window.CURRENT_LANGUAGE``
 *   come from ``data-*`` attributes on the bootstrap element (passed by
 *   ``schedule_hub.html``).
 *
 * Idempotent: re-running just merges fresh values on top.
 */
function hydrateGlobalsFromBootstrap(rootEl) {
  const scheduleTranslations = readJsonScript('schedule-translations-map');
  const sharedTranslations = readJsonScript('schedule-shared-translations');
  window.SCHEDULE_TRANSLATIONS = Object.assign(
    window.SCHEDULE_TRANSLATIONS || {},
    scheduleTranslations,
  );
  window.TRANSLATIONS = Object.assign(
    window.TRANSLATIONS || {},
    sharedTranslations,
  );

  if (rootEl) {
    const overdue = parseInt(rootEl.dataset.overdueThresholdMinutes, 10);
    if (!Number.isNaN(overdue)) {
      window.OVERDUE_THRESHOLD_MINUTES = overdue;
    }
    if (rootEl.dataset.currentLanguage) {
      window.CURRENT_LANGUAGE = rootEl.dataset.currentLanguage;
    }
  }
}

// ============================================================
// SHARED UTILITIES
// ============================================================

/**
 * Get CSRF token from a hidden ``[name=csrfmiddlewaretoken]`` input
 * (most reliable) or fall back to the ``csrftoken`` cookie.
 */
function getCsrfToken() {
  const input = document.querySelector('[name=csrfmiddlewaretoken]');
  if (input) return input.value;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, 10) === 'csrftoken=') {
        return decodeURIComponent(cookie.substring(10));
      }
    }
  }
  return null;
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** Escape for use inside a JavaScript-attribute string (onclick=, etc.). */
function escapeJsAttr(text) {
  if (!text) return '';
  return String(text)
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '&quot;');
}

// ============================================================
// OVERFLOW HELPER — "+N more" for dense Team grid cells
// ============================================================
//
// Used by Team View (cleaner × day matrix). The Calendar (month grid)
// uses FullCalendar's native ``dayMaxEvents``/popover instead.
//
// Usage:
//   ScheduleHub.overflow.render(container, events, {
//       max: 4,                              // optional, defaults to config
//       renderEvent: (ev) => HTMLElement|string,
//       moreKey: 'cleaner-7:2026-04-21',     // stable id for this group
//       moreLabelFn: (hidden) => 'Show 3 more visits',
//       onMoreClick: ({moreKey, hiddenEvents, allEvents, anchor}) => {}
//   });
//
// All events are appended to the DOM. Events past ``max`` get ``.hidden``
// (Tailwind) applied so they remain valid drop targets for HTML5 DnD
// but aren't visible until the user opens the day-detail drawer.

function overflowRender(container, events, options) {
  if (!container) return { visibleCount: 0, hiddenCount: 0 };
  options = options || {};
  const max = options.max || (window.ScheduleHub.config && window.ScheduleHub.config.maxVisibleEvents) || 4;
  const renderEvent = options.renderEvent;
  if (typeof renderEvent !== 'function') {
    throw new Error('ScheduleHub.overflow.render: options.renderEvent is required');
  }

  events = events || [];

  // Re-render safe: nuke any previous overflow chip in this container.
  const oldChip = container.querySelector(':scope > .schedule-more-link');
  if (oldChip) oldChip.remove();

  for (let i = 0; i < events.length; i++) {
    const rendered = renderEvent(events[i]);
    let node;
    if (rendered instanceof HTMLElement) {
      node = rendered;
    } else if (typeof rendered === 'string') {
      const tmp = document.createElement('div');
      tmp.innerHTML = rendered;
      node = tmp.firstElementChild;
    } else {
      continue;
    }
    if (!node) continue;
    if (i >= max) {
      node.classList.add('hidden');
      node.setAttribute('data-overflow-hidden', '1');
    }
    container.appendChild(node);
  }

  const hiddenCount = Math.max(0, events.length - max);
  if (hiddenCount > 0) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'schedule-more-link';
    const label = options.moreLabelFn
      ? options.moreLabelFn(hiddenCount)
      : ('+' + hiddenCount + ' ' + ((typeof window.gettext === 'function') ? window.gettext('more') : 'more'));
    chip.textContent = label;
    chip.setAttribute('aria-label', label);
    if (options.moreKey) {
      chip.setAttribute('data-more-key', options.moreKey);
    }
    chip.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof options.onMoreClick === 'function') {
        options.onMoreClick({
          moreKey: options.moreKey || null,
          hiddenEvents: events.slice(max),
          allEvents: events,
          anchor: chip,
        });
      }
    });
    container.appendChild(chip);
  }

  return {
    visibleCount: Math.min(max, events.length),
    hiddenCount: hiddenCount,
  };
}

// ============================================================
// DAY DETAIL DRAWER — Expand overflow on demand
// ============================================================
//
// Slide-in panel used by Team to show the full list of visits for a
// given (cleaner, date) slice. Month uses FC popover.
//
// Usage:
//   ScheduleHub.dayDetail.show({
//       title: 'Tuesday 21 April — Emma Bernard',
//       events: [...],
//       renderEvent: (ev) => HTMLElement|string
//   });
//
// The drawer markup lives in ``templates/office/schedule/_day_detail_drawer.html``
// and uses ``[data-day-detail-close]`` on every dismiss surface. The
// click + Escape global listeners are attached ONCE per session via
// the ``_dayDetailListenersBootstrapped`` flag.

function getDayDetailDrawer() {
  return document.getElementById('dayDetailDrawer');
}

function dayDetailShow(opts) {
  const drawer = getDayDetailDrawer();
  if (!drawer) {
    console.warn('ScheduleHub.dayDetail: #dayDetailDrawer not in DOM; include _day_detail_drawer.html');
    return;
  }
  opts = opts || {};

  const titleEl = drawer.querySelector('[data-day-detail-title]');
  const countEl = drawer.querySelector('[data-day-detail-count]');
  const bodyEl = drawer.querySelector('[data-day-detail-body]');

  if (titleEl) titleEl.textContent = opts.title || '';
  if (countEl) {
    const n = (opts.events || []).length;
    countEl.textContent = n;
  }

  if (bodyEl) {
    bodyEl.innerHTML = '';
    const events = opts.events || [];
    if (!events.length) {
      const empty = document.createElement('div');
      empty.className = 'py-6 text-center text-sm text-gray-500 dark:text-gray-400';
      empty.textContent = (typeof window.gettext === 'function') ? window.gettext('No visits') : 'No visits';
      bodyEl.appendChild(empty);
    } else if (typeof opts.renderEvent === 'function') {
      for (let i = 0; i < events.length; i++) {
        const rendered = opts.renderEvent(events[i]);
        let node;
        if (rendered instanceof HTMLElement) {
          node = rendered;
        } else if (typeof rendered === 'string') {
          const tmp = document.createElement('div');
          tmp.innerHTML = rendered;
          node = tmp.firstElementChild;
        }
        if (node) {
          // In the drawer, never hide — user opened this to see them all.
          node.classList.remove('hidden');
          node.removeAttribute('data-overflow-hidden');
          bodyEl.appendChild(node);
        }
      }
    }
  }

  drawer.classList.remove('translate-x-full');
  drawer.setAttribute('aria-hidden', 'false');
  const backdrop = document.getElementById('dayDetailDrawerBackdrop');
  if (backdrop) backdrop.classList.remove('hidden');
}

function dayDetailHide() {
  const drawer = getDayDetailDrawer();
  if (!drawer) return;
  drawer.classList.add('translate-x-full');
  drawer.setAttribute('aria-hidden', 'true');
  const backdrop = document.getElementById('dayDetailDrawerBackdrop');
  if (backdrop) backdrop.classList.add('hidden');
}

function bootstrapDayDetailListeners() {
  if (_dayDetailListenersBootstrapped) return;
  _dayDetailListenersBootstrapped = true;

  document.addEventListener('click', function(e) {
    const target = e.target;
    if (target && target.closest && target.closest('[data-day-detail-close]')) {
      dayDetailHide();
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      const drawer = getDayDetailDrawer();
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
        dayDetailHide();
      }
    }
  });
}

// ============================================================
// WEBSOCKET — Real-Time Schedule Updates
// ============================================================
//
// The actual WebSocket connection is owned by ``utils/realtime.js``
// (one socket per session, opened by ``bootstrapRealtime()`` from
// app.js). We only:
//   - Subscribe to the ``ws:schedule_changed`` ``CustomEvent`` it
//     re-emits on document.
//   - Debounce 500 ms so a burst of concurrent edits coalesces into a
//     single refetch.
//   - Expose ``ScheduleHub.utils.skipNextWsRefresh()`` for the Team
//     drag/drop path that already does an optimistic local update and
//     doesn't want the broadcast to trigger a redundant refetch.

function bootstrapScheduleWsListener() {
  if (_wsListenerBound) return;
  _wsListenerBound = true;

  let debounceTimer = null;
  let skipNextRefresh = false;

  function triggerRefresh() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function() {
      if (skipNextRefresh) {
        skipNextRefresh = false;
        return;
      }
      const r = window.ScheduleHub.refresh;
      if (r.calendar) r.calendar();
      if (r.team) r.team();
    }, 500);
  }

  document.addEventListener('ws:schedule_changed', triggerRefresh);

  // ws:reconnected catch-up. Channels broadcasts are fire-and-forget,
  // so any ``schedule_changed`` events the server pushed while THIS
  // socket was disconnected (typical case: the dispatcher's machine
  // briefly lost Wi-Fi, or the cleaner-mobile PWA replayed its offline
  // queue during a window where the office tab's WS happened to also
  // be reconnecting on the same shared connection) were silently
  // dropped before the dispatcher got them. On reconnect we don't
  // know WHICH events we missed, so the safe + cheap fix is to treat
  // a reconnect identically to a fresh ``schedule_changed`` — the
  // 500 ms debounce coalesces it with any other event arriving in
  // the same tick, and ``refetch*`` only re-pulls the currently
  // visible date range.
  //
  // This is the push-based replacement for the 30 s ``setInterval``
  // refetch the previous attempt added: no polling, no wasted
  // requests when nothing changed, AND a reliable catch-up on every
  // disconnect window. PO 2026-05-17 evening: "quand je suis online
  // toute les action se voit en ligne sur le calendrier ... pourquoi
  // faire du refetch toutes les 30 secondes ?".
  document.addEventListener('ws:reconnected', triggerRefresh);

  /**
   * Call before making a user-initiated schedule change to prevent
   * the broadcast from causing a redundant double-refresh.
   */
  window.ScheduleHub.utils.skipNextWsRefresh = function() {
    skipNextRefresh = true;
    setTimeout(function() { skipNextRefresh = false; }, 3000);
  };
}

// ============================================================
// PUBLIC INIT
// ============================================================

export function initScheduleShared(rootEl) {
  ensureNamespace();
  hydrateGlobalsFromBootstrap(rootEl);

  // Assign utility / drawer functions onto the namespace. These
  // assignments are idempotent — re-running them only overwrites with
  // the same function references.
  const SH = window.ScheduleHub;
  SH.utils.getCsrfToken = getCsrfToken;
  SH.utils.escapeHtml = escapeHtml;
  SH.utils.escapeJsAttr = escapeJsAttr;
  SH.overflow.render = overflowRender;
  SH.dayDetail.show = dayDetailShow;
  SH.dayDetail.hide = dayDetailHide;

  // Side effects — guarded against multiple initialisation.
  bootstrapDayDetailListeners();
  bootstrapScheduleWsListener();
}

export function autoInitScheduleShared() {
  const rootEl = document.querySelector('[data-auto-init="scheduleShared"]');
  initScheduleShared(rootEl);
}
